import type { Vec3Tuple } from './math-types';

export type VehicleMotionAnglesDeg = {
    pitch: number | null;
    roll: number | null;
    yaw: number | null;
};

export type VehicleMotionTyreSample = {
    slipAngleDeg: number | null;
    wheelCenter: Vec3Tuple | null;
    phiDeg: number | null;
};

export type VehicleMotionCsvSample = {
    /** Row number in the original CSV (1-based, as displayed in spreadsheet editors). */
    sourceRow: number;

    /** Raw time value as read from the CSV (seconds). */
    tRawSec: number;

    /** Time value after optional normalization (seconds). */
    tSec: number;

    body: {
        position: Vec3Tuple | null;
        anglesDeg: VehicleMotionAnglesDeg;
    };

    tyres: [VehicleMotionTyreSample, VehicleMotionTyreSample, VehicleMotionTyreSample, VehicleMotionTyreSample];
};

export type VehicleMotionCsvMeta = {
    vehicleNo: number | null;
    /** Optional time offset from metadata row (seconds). */
    timeOffsetSec: number | null;
    timeUnits: string | null;
};

export type ParsedVehicleMotionCsv = {
    meta: VehicleMotionCsvMeta;
    header: {
        columns: string[];
        index: Map<string, number>;
    };
    samples: VehicleMotionCsvSample[];
    warnings: string[];
};

export type ParseVehicleMotionCsvOptions = {
    /**
     * When true, subtracts the first sample's time from all samples.
     * This makes the first sample start at t=0.
     */
    zeroTimeToFirstSample?: boolean;
};

const normalizeHeader = (value: string) => {
    // Normalize CSV header cells into stable lookup keys.
    // Goals:
    // - tolerate leading BOM
    // - tolerate whitespace / mixed-case
    // - tolerate headers using spaces instead of underscores (e.g. "COG X" -> "cog_x")
    // - tolerate minor vendor variants (e.g. "tire" vs "tyre")
    const raw = value.replace(/^\uFEFF/, '').trim().toLowerCase();
    if (!raw) return '';

    // Collapse any non-alphanumeric runs into underscores.
    // Examples:
    // - "COG X" -> "cog_x"
    // - "ANGLE-PITCH" -> "angle_pitch"
    // - "TYRE_WCX1" -> "tyre_wcx1"
    let key = raw
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');

    // Common synonym normalization.
    // Keep this small and predictable; it's for real-world CSV variance.
    if (key === 'time' || key === 'timestamp' || key === 'seconds' || key === 'second' || key === 'sec') {
        key = 't';
    }

    // British/American spelling.
    // (Only apply when it looks like a tyre/tire prefixed column.)
    if (key.startsWith('tire_')) {
        key = `tyre_${key.slice('tire_'.length)}`;
    }

    // Compact COG axis variants.
    if (key === 'cogx') key = 'cog_x';
    if (key === 'cogy') key = 'cog_y';
    if (key === 'cogz') key = 'cog_z';

    return key;
};

const isRowEmpty = (row: string[]) => {
    for (const cell of row) {
        if (cell.trim() !== '') return false;
    }
    return true;
};

const finiteNumberRegex = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i;

const parseFiniteNumber = (raw: unknown): number | null => {
    const trimmed = String(raw ?? '').trim();
    if (!trimmed) return null;
    if (!finiteNumberRegex.test(trimmed)) return null;
    const v = Number(trimmed);
    return Number.isFinite(v) ? v : null;
};

const safeGet = (row: string[], idx: number | undefined): string => {
    if (idx === undefined) return '';
    return row[idx] ?? '';
};

const makeEmptyTyre = (): VehicleMotionTyreSample => ({
    slipAngleDeg: null,
    wheelCenter: null,
    phiDeg: null
});

// Minimal CSV line parser supporting quoted fields and escaped quotes.
// This is intentionally small and dependency-free.
type ParsedCsvLine = {
    cells: string[];
    unterminatedQuote: boolean;
};

const parseCsvLine = (line: string): ParsedCsvLine => {
    const out: string[] = [];
    let cur = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const ch = line[i];

        if (inQuotes) {
            if (ch === '"') {
                const next = line[i + 1];
                if (next === '"') {
                    cur += '"';
                    i++;
                } else {
                    inQuotes = false;
                }
            } else {
                cur += ch;
            }
            continue;
        }

        if (ch === ',') {
            out.push(cur);
            cur = '';
            continue;
        }

        if (ch === '"' && cur.trim() === '') {
            // Allow quotes to start a quoted field (even if we've accumulated whitespace).
            // Clear any whitespace accumulated before the quote to match typical CSV behavior.
            cur = '';
            inQuotes = true;
            continue;
        }

        cur += ch;
    }

    out.push(cur);
    return { cells: out, unterminatedQuote: inQuotes };
};

const parseCsv = (text: string): { rows: string[][]; warnings: string[] } => {
    const lines = String(text ?? '').split(/\r?\n/);
    const warnings: string[] = [];
    const rows: string[][] = [];

    for (let i = 0; i < lines.length; i++) {
        const parsed = parseCsvLine(lines[i]);
        rows.push(parsed.cells);
        if (parsed.unterminatedQuote) {
            warnings.push(`Row ${i + 1}: unterminated quote in CSV line.`);
        }
    }

    return { rows, warnings };
};

const parseMetaRow = (row: string[]): VehicleMotionCsvMeta => {
    // Example:
    // vehicle no,4,,,t,-2.04281E-14,s
    const lower = row.map(c => normalizeHeader(c));

    let vehicleNo: number | null = null;
    let timeOffsetSec: number | null = null;
    let timeUnits: string | null = null;

    for (let i = 0; i < lower.length; i++) {
        // Example meta headers vary: "vehicle no", "vehicle_no", etc.
        if (lower[i] === 'vehicle_no' || lower[i] === 'vehicleno') {
            vehicleNo = parseFiniteNumber(row[i + 1] ?? '') ?? vehicleNo;
        }
        if (lower[i] === 't') {
            timeOffsetSec = parseFiniteNumber(row[i + 1] ?? '') ?? timeOffsetSec;
            timeUnits = (row[i + 2] ?? '').trim() || timeUnits;
        }
    }

    return { vehicleNo, timeOffsetSec, timeUnits };
};

const buildHeaderIndex = (headerRow: string[]) => {
    const columns = headerRow.map(c => c.replace(/^\uFEFF/, '').trim());
    const index = new Map<string, number>();

    for (let i = 0; i < columns.length; i++) {
        const key = normalizeHeader(columns[i]);
        if (!key) continue;
        // Keep the first occurrence.
        if (!index.has(key)) index.set(key, i);
    }

    return { columns, index };
};

export function parseVehicleMotionCsv(text: string, options: ParseVehicleMotionCsvOptions = {}): ParsedVehicleMotionCsv {
    const warnings: string[] = [];

    const parsedCsv = parseCsv(text);
    const rows = parsedCsv.rows;
    warnings.push(...parsedCsv.warnings);

    // Find header row.
    // Prefer a header row that begins with "t", but allow headers where "t" appears later.
    const candidateHeaderIndexes: number[] = [];
    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        if (!row.length || isRowEmpty(row)) continue;
        const normalized = new Set(row.map(c => normalizeHeader(c)).filter(Boolean));
        if (normalized.has('t') && normalized.has('cog_x')) {
            candidateHeaderIndexes.push(i);
        }
    }

    const headerRowIndex = candidateHeaderIndexes.find(i => normalizeHeader(rows[i][0] ?? '') === 't') ?? candidateHeaderIndexes[0] ?? -1;

    if (headerRowIndex < 0) {
        return {
            meta: { vehicleNo: null, timeOffsetSec: null, timeUnits: null },
            header: { columns: [], index: new Map() },
            samples: [],
            warnings: ['Could not find CSV header row (expected a row beginning with "t" and containing "COG_X").']
        };
    }

    // Parse metadata (best-effort): scan all non-empty rows before the header.
    const meta: VehicleMotionCsvMeta = { vehicleNo: null, timeOffsetSec: null, timeUnits: null };
    for (let i = 0; i < headerRowIndex; i++) {
        if (isRowEmpty(rows[i])) continue;
        const partial = parseMetaRow(rows[i]);
        if (partial.vehicleNo !== null) meta.vehicleNo = partial.vehicleNo;
        if (partial.timeOffsetSec !== null) meta.timeOffsetSec = partial.timeOffsetSec;
        if (partial.timeUnits !== null) meta.timeUnits = partial.timeUnits;
    }

    const header = buildHeaderIndex(rows[headerRowIndex]);

    const idxT = header.index.get('t');
    const idxCogX = header.index.get('cog_x');
    const idxCogY = header.index.get('cog_y');
    const idxCogZ = header.index.get('cog_z');

    const idxPitch = header.index.get('angle_pitch');
    const idxRoll = header.index.get('angle_roll');
    const idxYaw = header.index.get('angle_yaw');

    if (idxT === undefined) {
        warnings.push('Missing required column "t".');
        return {
            meta,
            header,
            samples: [],
            warnings
        };
    }
    if (idxCogX === undefined || idxCogY === undefined || idxCogZ === undefined) warnings.push('Missing one or more required columns: COG_X, COG_Y, COG_Z.');

    // Pre-resolve tyre column indices once per parse (if present).
    const tyreIndexes = [1, 2, 3, 4].map(n => ({
        idxSa: header.index.get(`tyre_sa${n}`),
        idxWcx: header.index.get(`tyre_wcx${n}`),
        idxWcy: header.index.get(`tyre_wcy${n}`),
        idxWcz: header.index.get(`tyre_wcz${n}`),
        idxPhi: header.index.get(`tyre_phi${n}`)
    }));

    const samples: VehicleMotionCsvSample[] = [];
    let lastTRaw: number | null = null;

    for (let i = headerRowIndex + 1; i < rows.length; i++) {
        const row = rows[i];
        if (!row.length || isRowEmpty(row)) continue;

        const tRawSec = parseFiniteNumber(safeGet(row, idxT));
        if (tRawSec === null) {
            // If there is content but the time cell isn't parseable, warn and skip.
            warnings.push(`Row ${i + 1}: could not parse time (t).`);
            continue;
        }

        if (lastTRaw !== null) {
            if (tRawSec < lastTRaw) {
                warnings.push(`Row ${i + 1}: time went backwards (${tRawSec} < ${lastTRaw}).`);
            } else if (tRawSec === lastTRaw) {
                warnings.push(`Row ${i + 1}: duplicate time value (${tRawSec}).`);
            }
        }
        lastTRaw = tRawSec;

        const px = parseFiniteNumber(safeGet(row, idxCogX));
        const py = parseFiniteNumber(safeGet(row, idxCogY));
        const pz = parseFiniteNumber(safeGet(row, idxCogZ));
        const position: Vec3Tuple | null = (px !== null && py !== null && pz !== null) ? [px, py, pz] : null;

        const anglesDeg: VehicleMotionAnglesDeg = {
            pitch: parseFiniteNumber(safeGet(row, idxPitch)),
            roll: parseFiniteNumber(safeGet(row, idxRoll)),
            yaw: parseFiniteNumber(safeGet(row, idxYaw))
        };

        const tyres: VehicleMotionCsvSample['tyres'] = [makeEmptyTyre(), makeEmptyTyre(), makeEmptyTyre(), makeEmptyTyre()];

        for (let ti = 0; ti < 4; ti++) {
            const { idxSa, idxWcx, idxWcy, idxWcz, idxPhi } = tyreIndexes[ti];

            const wx = parseFiniteNumber(safeGet(row, idxWcx));
            const wy = parseFiniteNumber(safeGet(row, idxWcy));
            const wz = parseFiniteNumber(safeGet(row, idxWcz));

            tyres[ti] = {
                slipAngleDeg: parseFiniteNumber(safeGet(row, idxSa)),
                wheelCenter: (wx !== null && wy !== null && wz !== null) ? [wx, wy, wz] : null,
                phiDeg: parseFiniteNumber(safeGet(row, idxPhi))
            };
        }

        samples.push({
            sourceRow: i + 1,
            tRawSec,
            tSec: tRawSec,
            body: { position, anglesDeg },
            tyres
        });
    }

    const zeroTimeToFirstSample = options.zeroTimeToFirstSample ?? true;
    if (zeroTimeToFirstSample && samples.length > 0) {
        const t0 = samples[0].tRawSec;
        for (const s of samples) {
            s.tSec = s.tRawSec - t0;
        }
    }

    if (samples.length === 0) {
        warnings.push('No data samples found after header row.');
    }

    return {
        meta,
        header,
        samples,
        warnings
    };
}
